import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  BookOpen,
  GraduationCap,
  Users,
  AlertCircle,
  BarChart3,
  Target,
} from "lucide-react";
import image from "../../assets/childimage.png";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/child-education`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Child Education | SEVAA Foundation",
  alternateName: "Shiksha Na Ruke - Child Education Support",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports child education in India through learning resources, school admission support, after-school support and education access for poor, tribal and rural children.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Child Education",
    "Tribal Children Education",
    "Rural Education",
    "Poor Children Education",
    "School Admission Support",
    "Learning Resources",
    "After School Support",
    "Free Tuition",
    "Education Donation",
    "Books Donation",
    "Stationery Donation"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Child Education Support Program",
  serviceType: "Education Support for Underprivileged Children",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation helps underprivileged, rural and tribal children with school access, learning resources, study materials, free tuition and support to continue education.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Child Education",
      item: pageUrl,
    },
  ],
};

const ChildEducation: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Child Education NGO | SEVAA Foundation</title>

        <meta
          name="title"
          content="Child Education NGO | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Donate to SEVAA Foundation and support child education for poor, tribal and rural children through school access, books, study material and after-school support."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Child Education NGO | Donate for Poor Children" />
        <meta property="og:description" content="Support school access, books, stationery and learning support for poor, tribal and rural children through SEVAA Foundation." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation child education support for poor tribal and rural children" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Child Education NGO | SEVAA Foundation" />
        <meta name="twitter:description" content="Support SEVAA Foundation's child education mission for poor, rural and tribal children in India." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation child education support for poor tribal and rural children" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-0">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="child-education-heading"
          className="relative w-full h-[450px] md:h-[550px]"
        >
          <div className="absolute inset-0">
            <img
              src="https://unicointl.com/government/public/frontend/assets/images/school-students.png"
              alt="SEVAA Foundation child education support for poor tribal and rural children"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 opacity-60 bg-black"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
                Shiksha Na Ruke
              </span>

              <h1
                id="child-education-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Child Education in India <br />
                <span className="text-orange-400">A Critical Challenge</span>
              </h1>

              <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                Despite progress in schooling and literacy, child education
                remains a major struggle for millions of children, especially
                those living in poverty or in remote tribal and rural
                communities.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="education-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="education-reality-heading" className="sr-only">
            Child Education Reality in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.3 }}
              className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100 border-b-[6px] border-b-slate-200 hover:border-rose-200 hover:border-b-rose-500 hover:shadow-2xl hover:shadow-rose-500/20 transition-all duration-300 group cursor-pointer"
            >
              <AlertCircle className="w-12 h-12 text-rose-500 mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                206 <span className="text-xl text-slate-500">Million</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Children lack access to at least one basic essential service
                such as education, health or nutrition, showing why education
                support must go beyond classrooms.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100 border-b-[6px] border-b-slate-200 hover:border-amber-200 hover:border-b-amber-500 hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-300 group cursor-pointer"
            >
              <BarChart3 className="w-12 h-12 text-amber-500 mb-6 group-hover:scale-110 group-hover:translate-x-1 transition-transform duration-300" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                80.9<span className="text-xl text-slate-500">%</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                National literacy has improved, yet disparities continue
                between urban and rural areas, boys and girls, and poor and
                privileged families.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white p-8 rounded-3xl shadow-lg border border-teal-700 border-b-[6px] border-b-teal-900 hover:border-teal-500 hover:border-b-teal-400 hover:shadow-2xl hover:shadow-teal-900/50 transition-all duration-300 group cursor-pointer"
            >
              <Target className="w-12 h-12 text-teal-300 mb-6 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />

              <h3 className="text-2xl font-black mb-3 text-slate-900">
                High Dropouts
              </h3>

              <p className="text-slate-900 font-medium text-sm leading-relaxed">
                Economic pressures force children in tribal regions into work or
                household duties instead of structured schooling, increasing the
                risk of dropout.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="ground-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-teal-600"></span>
                <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                  The Core Issue
                </span>
              </div>

              <h2
                id="ground-reality-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Why structured schooling is still out of reach?
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                Large numbers of children still struggle to complete structured
                schooling, particularly in underserved regions where access to
                quality schools, trained teachers and learning resources is
                limited.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                Out-of-school children are disproportionately found in poorer
                households and tribal regions. The combination of lacking
                education, health and nutrition directly undermines a
                child&apos;s ability to learn and stay in school.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “Education is not just about literacy. It is the foundation
                  for future opportunity, improved livelihoods and breaking
                  cycles of chronic poverty.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
                alt="Tribal child education support and rural schooling by SEVAA Foundation"
                className="w-full h-full object-cover"
                loading="lazy"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-8">
                <p className="text-white font-medium text-lg leading-snug">
                  Ensuring that every child can complete quality education
                  directly supports SEVAA Foundation&apos;s mission to uplift
                  communities.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="education-intervention-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10 overflow-hidden"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16 max-w-3xl mx-auto"
            >
              <h2
                id="education-intervention-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                How SEVAA is Bridging the Gap
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                We focus on practical grassroots solutions to ensure that
                poverty does not dictate a child&apos;s future.
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-3 gap-8"
            >
              <motion.article
                whileHover={{ y: -15, scale: 1.02 }}
                className="bg-white p-8 rounded-[2rem] border-2 border-slate-100 group transition-colors duration-300 hover:border-orange-200 shadow-lg shadow-slate-200/50 hover:shadow-orange-500/10 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 rounded-bl-full -z-10 group-hover:scale-110 transition-transform duration-500"></div>

                <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                  <BookOpen className="w-8 h-8 text-orange-500 group-hover:text-white transition-colors duration-300" />
                </div>

                <h3 className="text-xl font-black text-slate-900 mb-3">
                  Learning Resources
                </h3>

                <p className="text-slate-600 font-medium leading-relaxed">
                  Providing free study material, books, bags and stationery to
                  underprivileged children who cannot afford basic school
                  supplies.
                </p>
              </motion.article>

              <motion.article
                whileHover={{ y: -15, scale: 1.02 }}
                className="bg-white p-8 rounded-[2rem] border-2 border-slate-100 group transition-colors duration-300 hover:border-teal-200 shadow-lg shadow-slate-200/50 hover:shadow-teal-500/10 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-bl-full -z-10 group-hover:scale-110 transition-transform duration-500"></div>

                <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <GraduationCap className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors duration-300" />
                </div>

                <h3 className="text-xl font-black text-slate-900 mb-3">
                  School Admissions
                </h3>

                <p className="text-slate-600 font-medium leading-relaxed">
                  Helping dropouts, street children and children from remote
                  tribal areas enroll and stay in formal schooling systems.
                </p>
              </motion.article>

              <motion.article
                whileHover={{ y: -15, scale: 1.02 }}
                className="bg-white p-8 rounded-[2rem] border-2 border-slate-100 group transition-colors duration-300 hover:border-amber-200 shadow-lg shadow-slate-200/50 hover:shadow-amber-500/10 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-bl-full -z-10 group-hover:scale-110 transition-transform duration-500"></div>

                <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-amber-500 transition-colors duration-300">
                  <Users className="w-8 h-8 text-amber-500 group-hover:text-white transition-colors duration-300" />
                </div>

                <h3 className="text-xl font-black text-slate-900 mb-3">
                  After-School Support
                </h3>

                <p className="text-slate-600 font-medium leading-relaxed">
                  Running free tuition and after-school support to help weak
                  students catch up and avoid dropout due to academic pressure.
                </p>
              </motion.article>
            </motion.div>
          </div>
        </section>

        {/* 5. CTA SECTION */}
        <section
          aria-labelledby="education-cta-heading"
          className="w-full bg-[#FFCD42] overflow-hidden mt-10"
        >
          <div className="grid md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col justify-center p-12 lg:p-20 xl:p-24 relative"
            >
              <h2
                id="education-cta-heading"
                className="text-4xl md:text-5xl lg:text-[3.5rem] font-black text-slate-900 leading-[1.1] mb-6 tracking-tight"
              >
                Better Future for <br /> Your Kids
              </h2>

              <p className="text-slate-800 font-semibold text-lg mb-8 opacity-90">
                Join SEVAA Foundation&apos;s educational network and support
                child education for underprivileged communities.
              </p>

              <form
                className="flex items-center bg-white p-2 rounded-full max-w-md shadow-2xl shadow-yellow-600/20"
                onSubmit={(event) => event.preventDefault()}
              >
                <input
                  type="email"
                  placeholder="Your Email"
                  aria-label="Enter your email to support SEVAA education network"
                  className="flex-1 bg-transparent border-none outline-none px-5 text-slate-800 placeholder:text-slate-400 font-medium w-full"
                />

                <button
                  type="submit"
                  className="w-12 h-12 flex-shrink-0 bg-[#6366F1] rounded-full flex items-center justify-center text-white hover:bg-[#4F46E5] transition-colors shadow-md hover:scale-105 duration-200"
                  aria-label="Submit email for SEVAA education support"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative h-[400px] md:h-[500px] lg:h-auto"
            >
              <img
                src={image}
                alt="Children studying in classroom supported by SEVAA Foundation education program"
                className="w-full h-full object-cover"
                loading="lazy"
              />

              <div className="absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-[#FFCD42] to-transparent hidden md:block"></div>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default ChildEducation;